import * as React from "react";

import { cn } from "../../lib/utils";

/** TimePicker: three 12-hour dropdowns over a canonical 24-hour value, with optional bounds and step. */

// "HH:MM" (24h) -> minutes since midnight | null when empty/invalid.
function toMin(value) {
  if (!value || typeof value !== "string") return null;
  const [hStr, mStr] = value.split(":");
  const h = Number(hStr);
  const m = Number(mStr);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

// minutes since midnight -> "HH:MM" (24h).
function toHHMM(mins) {
  return `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
}

// minutes since midnight -> { hour12, minute, period }.
function parts(mins) {
  const h = Math.floor(mins / 60);
  return {
    hour12: h % 12 === 0 ? 12 : h % 12,
    minute: mins % 60,
    period: h >= 12 ? "PM" : "AM",
  };
}

// { hour12, period } -> the 24-hour hour.
function hour24(hour12, period) {
  const h = hour12 % 12; // 12 -> 0
  return period === "PM" ? h + 12 : h;
}

const selectClass =
  "h-10 rounded-md border border-input bg-background px-2 py-2 text-sm " +
  "ring-offset-background focus-visible:outline-none focus-visible:ring-2 " +
  "focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed " +
  "disabled:opacity-50";

const TimePicker = React.forwardRef(
  ({ value, onChange, id, className, disabled, min, max, step = 1,
     keepOutOfRange = true }, ref) => {
    const current = toMin(value);

    /* Every time this field may hold, derived once per render so the three lists can never disagree. */
    const allowed = React.useMemo(() => {
      const lo = toMin(min) ?? 0;
      const hi = toMin(max) ?? 24 * 60 - 1;
      const inc = Math.max(1, Number(step) || 1);
      const out = [];
      // Snapped to the step's grid from midnight, so a 15-minute step always reads :00 :15 :30 :45.
      for (let t = Math.ceil(lo / inc) * inc; t <= hi; t += inc) out.push(t);
      return out;
    }, [min, max, step]);

    /* A legacy value outside the bounds is kept and marked, unless it is the very mistake the bounds prevent. */
    const strayValue = current != null && !allowed.includes(current);
    const outsideBounds = strayValue && keepOutOfRange;
    const options = React.useMemo(
      () => (outsideBounds ? [current, ...allowed].sort((a, b) => a - b) : allowed),
      [outsideBounds, current, allowed],
    );

    const chosen = current != null && !(strayValue && !keepOutOfRange)
      ? parts(current) : null;

    // Which periods, hours and minutes the options contain, as Sets so an hour offered twice is listed once.
    const periods = ["AM", "PM"].filter((p) =>
      options.some((t) => parts(t).period === p));
    const hours = [];
    options.forEach((t) => {
      const p = parts(t);
      if (chosen && p.period !== chosen.period) return;
      if (!hours.includes(p.hour12)) hours.push(p.hour12);
    });
    /* 1 to 12, in that order. Collected in the order the day offers them, a
       branch opening at nine listed 09 10 11 12 01 … 08 — the times in clock
       order, but nobody reads an hour box that way: it is a number, and a
       number list that starts at nine reads as broken. Sorted here rather than
       at the source, because the source order is what makes the AM/PM and
       minute lists agree with it. */
    hours.sort((a, b) => a - b);
    /* Minutes for the chosen hour, or every minute when none is chosen, so the boxes can be filled in any order. */
    const minutes = [];
    options.forEach((t) => {
      if (chosen && Math.floor(t / 60) !== hour24(chosen.hour12, chosen.period)) return;
      const m = t % 60;
      if (!minutes.includes(m)) minutes.push(m);
    });
    minutes.sort((a, b) => a - b);

    /* Changing one dropdown can name a disallowed time, so land on the nearest allowed minute in the chosen hour. */
    const emit = (next) => {
      const base = chosen || parts(options[0] ?? 0);
      const merged = { ...base, ...next };
      const wanted = hour24(merged.hour12, merged.period) * 60 + merged.minute;
      if (options.includes(wanted)) { onChange(toHHMM(wanted)); return; }
      const sameHour = options.filter(
        (t) => Math.floor(t / 60) === hour24(merged.hour12, merged.period));
      const pool = sameHour.length ? sameHour : options;
      if (!pool.length) return;
      const nearest = pool.reduce(
        (best, t) => (Math.abs(t - wanted) < Math.abs(best - wanted) ? t : best), pool[0]);
      onChange(toHHMM(nearest));
    };

    return (
      <div ref={ref} id={id} className={cn("flex items-center gap-1.5", className)}>
        <select
          aria-label="Hour"
          className={selectClass}
          disabled={disabled}
          value={chosen ? chosen.hour12 : ""}
          onChange={(e) => emit({ hour12: Number(e.target.value) })}
        >
          <option value="" disabled>--</option>
          {hours.map((h) => (
            <option key={h} value={h}>{String(h).padStart(2, "0")}</option>
          ))}
        </select>
        <span className="text-sm text-muted-foreground">:</span>
        <select
          aria-label="Minute"
          className={selectClass}
          disabled={disabled}
          value={chosen ? chosen.minute : ""}
          onChange={(e) => emit({ minute: Number(e.target.value) })}
        >
          <option value="" disabled>--</option>
          {minutes.map((m) => (
            <option key={m} value={m}>{String(m).padStart(2, "0")}</option>
          ))}
        </select>
        <select
          aria-label="AM/PM"
          className={selectClass}
          disabled={disabled}
          /* Empty until a time exists — an untouched field reading "AM" looks
             half-answered, and the required mark then lies about what is set. */
          value={chosen ? chosen.period : ""}
          onChange={(e) => emit({ period: e.target.value })}
        >
          <option value="" disabled>--</option>
          {periods.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        {outsideBounds && (
          <span className="text-xs text-amber-600 whitespace-nowrap">
            outside opening hours
          </span>
        )}
      </div>
    );
  },
);
TimePicker.displayName = "TimePicker";

export { TimePicker };
